import { useState } from 'react'

import { ПРИБОРЫ_ДЛИНЫ, measureWith, ru, significantDigits } from '../../lib/physics'
import { Choice, Predict } from './common'

/**
 * Одна монета — четыре прибора (урок 7): диаметр монеты 20,59 мм меряют мерной лентой,
 * линейкой, штангенциркулем и микрометром. Чем точнее прибор, тем уже полоса, где лежит
 * настоящее значение, и тем больше значащих цифр в результате. Точных измерений не бывает.
 */

const ИСТИНА = 20.59 // мм — диаметр по микрометру из урока
const X0 = 20
const PX_PER_MM = 50 // окно рисунка: от 17 до 24 мм
const ОТ = 17.2

export default function CoinLab() {
  const [номер, setНомер] = useState(0)
  const [замеры, setЗамеры] = useState<number[]>([0])

  const п = ПРИБОРЫ_ДЛИНЫ[номер]
  const знаков = п.деление >= 1 ? 0 : п.деление >= 0.1 ? 1 : 2
  const отсчёт = measureWith(ИСТИНА, п.деление)
  const запись = ru(отсчёт, знаков)
  const x = (мм: number) => X0 + (мм - ОТ) * PX_PER_MM

  const выбрать = (n: number) => {
    setНомер(n)
    setЗамеры((z) => (z.includes(n) ? z : [...z, n]))
  }

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 150" role="img" aria-label="Полосы точности четырёх приборов">
        {[18, 19, 20, 21, 22, 23].map((мм) => (
          <g key={мм}>
            <line x1={x(мм)} y1={120} x2={x(мм)} y2={126} className="ink-muted" />
            <text x={x(мм)} y={138} textAnchor="middle" className="lab-tick">{мм} мм</text>
          </g>
        ))}
        <line x1={x(ИСТИНА)} y1={8} x2={x(ИСТИНА)} y2={120} className="ink-muted" strokeDasharray="3 3" />
        {ПРИБОРЫ_ДЛИНЫ.map((пр, i) => {
          const о = measureWith(ИСТИНА, пр.деление)
          const видно = замеры.includes(i)
          const y = 18 + i * 26
          return видно ? (
            <g key={пр.прибор}>
              <rect x={x(о - пр.точность)} y={y} width={Math.max(2 * пр.точность * PX_PER_MM, 2)} height={14} rx={3} className={i === номер ? 'fill-heavy' : 'fill-unit-a'} />
              <text x={x(о + пр.точность) + 6} y={y + 11} className="lab-tick">{пр.прибор}</text>
            </g>
          ) : null
        })}
      </svg>

      <div className="lab-big">
        d = {запись} мм <span className="muted small">± {ru(п.точность, знаков === 0 ? 1 : знаков)} мм</span>
      </div>
      <div className="lab-readout">
        <span>прибор: {п.прибор}</span>
        <span>значащих цифр: <b>{significantDigits(запись)}</b></span>
        <span>настоящее значение лежит от {ru(отсчёт - п.точность, знаков + 1)} до {ru(отсчёт + п.точность, знаков + 1)} мм</span>
      </div>

      <div className="lab-sub">Чем меряем</div>
      <Choice options={ПРИБОРЫ_ДЛИНЫ.map((x2, i) => ({ value: i, label: x2.прибор }))} value={номер} onChange={выбрать} />

      <Predict
        question="Каким прибором результат выйдет с наибольшим числом значащих цифр?"
        options={['мерной лентой', 'линейкой', 'микрометром']}
        right={2}
        revealed={замеры.length === ПРИБОРЫ_ДЛИНЫ.length}
        explain="Микрометр меряет до 0,01 мм — 20,59 мм, четыре значащие цифры. Чем точнее прибор, тем больше цифр в результате можно гарантировать."
      />
      <p className="muted small">
        Полоса — где лежит настоящий диаметр. Измерь всеми четырьмя приборами: полосы вкладываются друг в друга.
        Если полосы двух приборов едва касаются, виноваты погрешность прибора или сама монета — идеально круглых монет нет.
      </p>
    </div>
  )
}
